import { Schema } from "effect";

/**
 * Field schemas shared by the dashboard, metric, resolve and edit contracts.
 * Internal to `@t3tools/contracts`: the public names are re-exported by the
 * modules that own them.
 */

export const Identifier = Schema.String.check(
  Schema.isPattern(/^[a-z][a-z0-9_]{0,63}$/, {
    message: "Use lowercase letters, digits and underscores, starting with a letter",
  }),
);

export const Label = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120));
export const ShortText = Schema.String.check(Schema.isMaxLength(500));
/** Plain-language explanation of what a number counts. */
export const DefinitionText = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(2_000));

export const IsoDate = Schema.String.check(
  Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/, { message: "Expected a YYYY-MM-DD date" }),
);

/** An ISO 8601 instant with a time zone, e.g. `2026-10-08T06:00:00.000Z`. */
export const IsoTimestamp = Schema.String.check(
  Schema.isPattern(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/, {
    message: "Expected an ISO 8601 timestamp with a time zone",
  }),
);

export const Span = Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 12 }));
export const NonNegativeCount = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
export const PositiveCount = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1));

export const DashboardDateRangePreset = Schema.Literals([
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "month_to_date",
  "previous_month",
  "quarter_to_date",
  "year_to_date",
]);
export type DashboardDateRangePreset = typeof DashboardDateRangePreset.Type;

/**
 * A dataset value as JSON. `number` columns are numbers, `date` columns are
 * YYYY-MM-DD, `timestamp` columns are ISO strings, `boolean` columns are booleans.
 */
export const DashboardArtifactCell = Schema.Union([
  Schema.String,
  Schema.Number,
  Schema.Boolean,
  Schema.Null,
]);
export type DashboardArtifactCell = typeof DashboardArtifactCell.Type;

/** Where a path-addressed problem is, and what is wrong, in plain language. */
export type ReferenceIssue = {
  readonly path: ReadonlyArray<PropertyKey>;
  readonly message: string;
};

export function findDuplicate(values: ReadonlyArray<string>): string | undefined {
  const seen = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) return value;
    seen.add(value);
  }
  return undefined;
}
